// Apolo IDE — SidePanel: Tasks tab (Epic 11).
//
// Lists subagents dispatched from the active code session. Each row shows
// the task (truncated), status pill, model, and a short summary. The user
// can also kick off a quick subagent from a small composer at the top.

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Play, RefreshCw, XCircle } from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import TodosLiveList from './TodosLiveList.jsx';

const STATUS_TONE = {
  pending: 'bg-apolo-textTertiary/15 text-apolo-textTertiary',
  running: 'bg-apolo-accent/15 text-apolo-accent',
  success: 'bg-apolo-success/15 text-apolo-success',
  failed: 'bg-apolo-error/15 text-apolo-error',
  timeout: 'bg-apolo-warning/15 text-apolo-warning',
};

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_TONE[status] || STATUS_TONE.pending}`}>
      {status}
    </span>
  );
}

export default function TasksTab() {
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const listSubagents = useAgentStore((s) => s.listSubagents);
  const dispatchSubagent = useAgentStore((s) => s.dispatchSubagent);
  const [task, setTask] = useState('');
  const [subagents, setSubagents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(false);

  const refresh = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    setError(null);
    const res = await listSubagents(activeSessionId);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    setSubagents(res.subagents);
  }, [activeSessionId, listSubagents]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleDispatch(e) {
    e.preventDefault();
    if (!task.trim() || !activeSessionId) return;
    setDispatching(true);
    setError(null);
    const res = await dispatchSubagent(activeSessionId, { task: task.trim(), role: 'general' });
    setDispatching(false);
    if (!res.ok) {
      setError(res.error || 'dispatch falhou');
      return;
    }
    setTask('');
    setSubagents((prev) => [res.subagent, ...prev]);
  }

  if (!activeSessionId) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-6 text-center">
        <p className="max-w-xs text-sm text-apolo-textTertiary">
          Abra uma sessão para dispatch de subagents.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto px-4 py-4 text-sm">
      <TodosLiveList />

      <header className="flex items-center justify-between">
        <h3 className="text-apolo-textPrimary">Subagents</h3>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          aria-label="Atualizar lista"
          className="rounded-md p-1 text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw size={14} aria-hidden="true" />
          )}
        </button>
      </header>

      <form onSubmit={handleDispatch} className="flex items-stretch gap-1.5">
        <input
          type="text"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          placeholder="Tarefa pro subagent (ex: revise tests/ for missing edge cases)"
          className="flex-1 rounded-md border border-apolo-border bg-apolo-surface2 px-2.5 py-1.5 text-xs focus:border-apolo-accent focus:outline-none"
        />
        <button
          type="submit"
          disabled={dispatching || !task.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-2.5 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-60"
        >
          {dispatching ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <Play size={12} aria-hidden="true" />
          )}
          Dispatch
        </button>
      </form>

      {error && (
        <div role="alert" className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-xs text-apolo-error">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-2 overflow-auto">
        {subagents.length === 0 && (
          <p className="text-xs text-apolo-textTertiary">Nenhum subagent ainda.</p>
        )}
        {subagents.map((sub) => (
          <article
            key={sub.id}
            className="flex flex-col gap-1 rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-xs font-medium text-apolo-textPrimary" title={sub.task}>
                {sub.task}
              </span>
              <StatusBadge status={sub.status} />
            </div>
            <div className="flex items-center justify-between text-[10px] text-apolo-textTertiary">
              <span>{sub.model}</span>
              <span>
                in {sub.tokens_in} / out {sub.tokens_out}
              </span>
            </div>
            {sub.summary && (
              <p className="text-xs text-apolo-textSecondary">
                {sub.status === 'failed' ? (
                  <span className="inline-flex items-center gap-1 text-apolo-error">
                    <XCircle size={11} aria-hidden="true" />
                    {sub.summary}
                  </span>
                ) : sub.status === 'success' ? (
                  <span className="inline-flex items-center gap-1 text-apolo-success">
                    <CheckCircle2 size={11} aria-hidden="true" />
                    {sub.summary}
                  </span>
                ) : (
                  sub.summary
                )}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
